// Arbeitszeitnachweis time math, client twin of utils/attendance/timeline.js (live preview in
// the day editor; the server stays authoritative). Keep everything below the header identical
// to the server file: test/attendance/timeline.test.js compares both.

const SEGMENT_TYPES = ["WORK", "TRAVEL", "BREAK"];
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
// § 4 ArbZG: only interruptions of at least 15 minutes count as a rest break
const MIN_LEGAL_BREAK = 15;
// A clock/entry may be up to this far ahead of the server clock (device clock drift)
const FUTURE_TOLERANCE = 2 * MINUTE;
const MAX_SPAN = 24 * HOUR;

const toMs = value => {
  if (value === null || value === undefined || value === "") return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : NaN;
};

const idOfProject = project =>
  project && (project._id || project) ? String(project._id || project) : "";

// Sorted copies with start/end as epoch ms; the original index is kept for error reporting.
function normalizeSegments(segments) {
  return (Array.isArray(segments) ? segments : [])
    .map((s, index) => ({
      index,
      type: s && s.type,
      start: toMs(s && s.start),
      end: toMs(s && s.end),
      paid: s && s.type === "TRAVEL" ? s.paid !== false : true,
      project: idOfProject(s && s.project)
    }))
    .sort((a, b) => (a.start || 0) - (b.start || 0));
}

/**
 * Hard errors that make a day unsaveable: [{ code, index }].
 * options: { now, dayStart, dayEnd, previousEnd, nextStart, allowOpen }
 */
function validateSegments(segments, options) {
  const opts = options || {};
  const now = opts.now === undefined ? Date.now() : opts.now;
  const errors = [];
  const list = normalizeSegments(segments);
  list.forEach((s, i) => {
    if (!SEGMENT_TYPES.includes(s.type)) errors.push({ code: "INVALID_TYPE", index: s.index });
    if (s.start === null || Number.isNaN(s.start) || Number.isNaN(s.end)) {
      errors.push({ code: "INVALID_TIME", index: s.index });
      return;
    }
    if (s.end === null) {
      if (i !== list.length - 1) errors.push({ code: "OPEN_NOT_LAST", index: s.index });
      else if (!opts.allowOpen) errors.push({ code: "END_MISSING", index: s.index });
    } else if (s.end <= s.start) {
      errors.push({ code: "END_BEFORE_START", index: s.index });
    }
    if (Math.max(s.start, s.end || 0) > now + FUTURE_TOLERANCE)
      errors.push({ code: "IN_FUTURE", index: s.index });
    const prev = list[i - 1];
    if (prev && prev.end !== null && !Number.isNaN(prev.end) && s.start < prev.end)
      errors.push({ code: "OVERLAP", index: s.index });
  });
  if (errors.length || !list.length) return errors;

  const first = list[0];
  const last = list[list.length - 1];
  if (opts.dayStart != null && (first.start < opts.dayStart || first.start >= opts.dayEnd))
    errors.push({ code: "OUTSIDE_DAY", index: first.index });
  const lastEnd = last.end === null ? now : last.end;
  if (lastEnd - first.start > MAX_SPAN) errors.push({ code: "SPAN_TOO_LONG", index: last.index });
  if (opts.previousEnd != null && first.start < opts.previousEnd)
    errors.push({ code: "OVERLAPS_PREVIOUS_DAY", index: first.index });
  if (opts.nextStart != null && lastEnd > opts.nextStart)
    errors.push({ code: "OVERLAPS_NEXT_DAY", index: last.index });
  return errors;
}

/**
 * Totals and ArbZG warnings of one day (all durations in minutes).
 * options: { now (counts an open block up to now; null = ignore it), flatBreakMinutes,
 *            targetMinutes, previousEnd }
 */
function summarizeDay(segments, options) {
  const opts = options || {};
  const now = opts.now === undefined ? null : opts.now;
  const list = normalizeSegments(segments).filter(
    s => SEGMENT_TYPES.includes(s.type) && Number.isFinite(s.start)
  );
  const summary = {
    workMinutes: 0,
    travelPaidMinutes: 0,
    travelUnpaidMinutes: 0,
    breakMinutes: 0,
    flatBreakMinutes: Math.max(0, Number(opts.flatBreakMinutes) || 0),
    paidMinutes: 0,
    targetMinutes: Math.max(0, Number(opts.targetMinutes) || 0),
    overtimeMinutes: 0,
    balanceMinutes: 0,
    legalBreakMinutes: 0,
    firstStart: list.length ? list[0].start : null,
    lastEnd: null,
    open: null,
    projectMinutes: {},
    warnings: []
  };

  // Paid stretches, merged across touching blocks, to find breaks between them (§ 4 ArbZG)
  const stretches = [];
  for (const s of list) {
    let end = s.end;
    if (end === null || Number.isNaN(end)) {
      summary.open = { type: s.type, start: s.start, project: s.project };
      if (now === null || now <= s.start) continue;
      end = now;
    }
    const minutes = (end - s.start) / MINUTE;
    summary.lastEnd = summary.lastEnd === null ? end : Math.max(summary.lastEnd, end);
    if (s.type === "BREAK") {
      summary.breakMinutes += minutes;
      if (minutes >= MIN_LEGAL_BREAK) summary.legalBreakMinutes += minutes;
      continue;
    }
    if (s.type === "TRAVEL" && !s.paid) {
      summary.travelUnpaidMinutes += minutes;
      continue;
    }
    if (s.type === "WORK") summary.workMinutes += minutes;
    else summary.travelPaidMinutes += minutes;
    summary.projectMinutes[s.project] = (summary.projectMinutes[s.project] || 0) + minutes;
    const last = stretches[stretches.length - 1];
    if (last && s.start - last.end < MIN_LEGAL_BREAK * MINUTE) last.end = Math.max(last.end, end);
    else stretches.push({ start: s.start, end });
  }

  // Gaps between paid stretches are unpaid interruptions; breaks logged inside them are
  // already counted, so only the unlogged remainder adds up.
  for (let i = 1; i < stretches.length; i += 1) {
    const gapStart = stretches[i - 1].end;
    const gapEnd = stretches[i].start;
    const logged = list
      .filter(s => s.type === "BREAK" && s.end !== null && s.start >= gapStart && s.end <= gapEnd)
      .reduce((sum, s) => sum + (s.end - s.start) / MINUTE, 0);
    const unlogged = (gapEnd - gapStart) / MINUTE - logged;
    if (unlogged >= MIN_LEGAL_BREAK) summary.legalBreakMinutes += unlogged;
  }

  const gross = summary.workMinutes + summary.travelPaidMinutes;
  const flat = Math.min(summary.flatBreakMinutes, gross);
  summary.paidMinutes = gross - flat;
  summary.legalBreakMinutes += summary.flatBreakMinutes;
  // A lump-sum break reduces every project's share proportionally
  if (flat > 0 && gross > 0) {
    for (const key of Object.keys(summary.projectMinutes))
      summary.projectMinutes[key] *= (gross - flat) / gross;
  }
  summary.balanceMinutes = summary.targetMinutes ? summary.paidMinutes - summary.targetMinutes : 0;
  summary.overtimeMinutes = Math.max(0, summary.balanceMinutes);

  const paid = summary.paidMinutes;
  const legal = summary.legalBreakMinutes;
  const required = paid > 9 * 60 ? 45 : paid > 6 * 60 ? 30 : 0;
  if (required && legal < required)
    summary.warnings.push({ code: "BREAK_TOO_SHORT", required, actual: Math.round(legal) });
  const longest = stretches.reduce((max, s) => Math.max(max, s.end - s.start), 0) / MINUTE;
  if (longest > 6 * 60 && !summary.flatBreakMinutes)
    summary.warnings.push({ code: "WORK_WITHOUT_BREAK", minutes: Math.round(longest) });
  if (paid > 10 * 60)
    summary.warnings.push({ code: "MAX_DAILY_EXCEEDED", minutes: Math.round(paid) });
  if (opts.previousEnd != null && summary.firstStart !== null) {
    const rest = (summary.firstStart - opts.previousEnd) / MINUTE;
    if (rest >= 0 && rest < 11 * 60)
      summary.warnings.push({ code: "REST_TOO_SHORT", minutes: Math.round(rest) });
  }
  return summary;
}

export {
  SEGMENT_TYPES,
  MIN_LEGAL_BREAK,
  FUTURE_TOLERANCE,
  normalizeSegments,
  validateSegments,
  summarizeDay
};
